// src/context/DiscoveryContext.js
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
} from "react";
import api from "../api";
import { ensureSelfFirst } from "../utils/stories";

const DiscoveryContext = createContext(null);

// ---- Config ----
// ✅ bump cache key because semantics changed (following-only discovery + suggested layer in Home)
const CACHE_KEY = "bf_discovery_users_v5";

// Cache TTL: keep short so online dot + stories feel fresh
const CACHE_TTL_MS = 60 * 1000; // 60s

let inFlight = null; // de-dupe concurrent/StrictMode fetches

const readCache = () => {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw); // { data, ts }
    const data = Array.isArray(parsed?.data) ? parsed.data : [];

    // ✅ IMPORTANT: if cache is empty array, treat as NO CACHE
    // otherwise Home shows blank space before fetch sets loading=true
    if (data.length === 0) return null;

    return { data, ts: parsed?.ts || 0 };
  } catch {
    return null;
  }
};

const writeCache = (data) => {
  try {
    const arr = Array.isArray(data) ? data : [];

    // ✅ Don’t persist empty arrays; they cause the “blank gap” bug
    if (arr.length === 0) {
      localStorage.removeItem(CACHE_KEY);
      return;
    }

    localStorage.setItem(CACHE_KEY, JSON.stringify({ data: arr, ts: Date.now() }));
  } catch {}
};

export function DiscoveryProvider({ currentUser, children }) {
  const cached = readCache();

  // ✅ show cache instantly (if non-empty)
  const [users, setUsers] = useState(cached?.data || []);

  // ✅ loading should be true if we have NO non-empty cache
  const [loading, setLoading] = useState(!(cached?.data?.length > 0));

  const [error, setError] = useState(null);
  const tsRef = useRef(cached?.ts || 0);

  const buildList = useCallback(
    (fetched) => ensureSelfFirst(Array.isArray(fetched) ? fetched : [], currentUser),
    [currentUser]
  );

  const fetchDiscovery = useCallback(
    async ({ revalidate = false } = {}) => {
      const now = Date.now();
      const isFresh = now - tsRef.current < CACHE_TTL_MS;

      // If we have users and cache still fresh, skip unless revalidate requested
      if (!revalidate && users.length && isFresh) return;

      if (inFlight) return inFlight;

      // ✅ if nothing is currently rendered, show loading (prevents “empty gap”)
      if (!users.length) setLoading(true);
      setError(null);

      const p = (async () => {
        try {
// ✅ Discovery row:
// backend returns self + following users + global admin accounts with active stories

          const res = await api.get(`/users/discovery`, {
            params: {
              mode: "following-only",
              username: currentUser?.username, // viewer username for seen/unseen logic if backend needs it
              limit: 30,
              excludeSelf: false,
            },
          });

          const fetched = Array.isArray(res.data) ? res.data : [];
          const ordered = buildList(fetched);

          // optional preload to reduce avatar pop-in
          await Promise.allSettled(preloadImages(ordered));

          setUsers(ordered);

          // ✅ only cache if non-empty
          writeCache(ordered);
          tsRef.current = Date.now();
        } catch (e) {
          // if we had no users, show error state
          if (!users.length) setError(e?.message || "Failed to load discovery users");
        } finally {
          setLoading(false);
          inFlight = null;
        }
      })();

      inFlight = p;
      return p;
    },
    [users.length, currentUser?.username, buildList]
  );

  // On mount / user change: show cache instantly; soft revalidate
  useEffect(() => {
    // If user not ready yet, keep loading (Home can show skeleton)
    if (!currentUser?.username) return;

    if (cached?.data?.length) {
      fetchDiscovery({ revalidate: true });
    } else {
      fetchDiscovery();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.username]);

  // If user hydrates after we already had a list, inject self immediately (no refetch needed)
  useEffect(() => {
    if (!currentUser) return;

    setUsers((prev) => {
      const next = ensureSelfFirst(prev || [], currentUser);
      if (next !== prev) writeCache(next);
      return next;
    });
  }, [currentUser?._id, currentUser?.username]);

  // Revalidate when the current user just created a story
  useEffect(() => {
    const onCreated = (e) => {
      const who = e?.detail?.username;
      if (!who || !currentUser?.username) return;

      if (who.toLowerCase() === currentUser.username.toLowerCase()) {
        fetchDiscovery({ revalidate: true });
      }
    };

    window.addEventListener("story:created", onCreated);
    return () => window.removeEventListener("story:created", onCreated);
  }, [currentUser?.username, fetchDiscovery]);

  // When a story owner is finished, DON'T remove them; just force rerender
  useEffect(() => {
    const onFinished = (e) => {
      const uname = e?.detail?.username;
      if (!uname) return;

      setUsers((prev) => {
        let changed = false;
        const next = (prev || []).map((u) => {
          if (u?.username?.toLowerCase() !== uname.toLowerCase()) return u;
          changed = true;
          return { ...u };
        });
        if (changed) writeCache(next);
        return next;
      });
    };

    window.addEventListener("bf:storyOwnerFinished", onFinished);
    return () => window.removeEventListener("bf:storyOwnerFinished", onFinished);
  }, []);

  // Revalidate on focus if cache expired
  useEffect(() => {
    const onFocus = () => {
      if (!currentUser?.username) return;

      if (Date.now() - tsRef.current >= CACHE_TTL_MS) {
        fetchDiscovery({ revalidate: true });
      }
    };

    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);

    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, [currentUser?.username, fetchDiscovery]);


  // ✅ Background refresh while user stays on Home
  // This keeps active stories/admin announcement stories fresh without pull-refresh.
  useEffect(() => {
    if (!currentUser?.username) return;

    const DISCOVERY_REFRESH_MS = 30 * 1000; // every 30 seconds

    const tick = () => {
      const isHome =
        window.location.pathname === "/home" ||
        window.location.pathname === "/";

      const isVisible = document.visibilityState !== "hidden";

      if (!isHome || !isVisible) return;

      fetchDiscovery({ revalidate: true });
    };

    const intervalId = setInterval(tick, DISCOVERY_REFRESH_MS);

    return () => clearInterval(intervalId);
  }, [currentUser?.username, fetchDiscovery]);


  const refetch = useCallback(
    () => fetchDiscovery({ revalidate: true }),
    [fetchDiscovery]
  );

  return (
    <DiscoveryContext.Provider
      value={{
        discoveryUsers: users,
        loading,
        error,
        lastFetched: tsRef.current,
        refetch,
      }}
    >
      {children}
    </DiscoveryContext.Provider>
  );
}

export function useDiscovery() {
  const ctx = useContext(DiscoveryContext);
  if (!ctx) throw new Error("useDiscovery must be used within a DiscoveryProvider");
  return ctx;
}

function preloadImages(users) {
  const urls = [];
  for (const u of users || []) {
    if (u?.profilePic) urls.push(u.profilePic);

    const media =
      u?.latestStory?.mediaUrl || u?.latestStory?.thumbnail || u?.latestStory?.url;

    if (
      media &&
      /\.(png|jpe?g|gif|webp|avif)$/i.test((String(media).split("?")[0] || ""))
    ) {
      urls.push(media);
    }
  }

  return urls.map(
    (src) =>
      new Promise((res) => {
        const img = new Image();
        img.onload = res;
        img.onerror = res;
        img.src = src;
      })
  );
}
